import { Fragment, type ReactElement } from 'react'

import { Box } from '@mui/material'

import type { NavItems, NavLink } from '@core/layouts/types'

export type FlattenedNavLink = NavLink & {
  path: string // Ensure path is required for flattened items
  breadcrumb?: string // Parent context for nested items (e.g. "Commerce & Billing")
}

/**
 * Checks if an icon is a placeholder (empty fragment or spacer box) rather than a meaningful icon.
 * Nav items often use <></> or <Box sx={{ width: 14 }} /> as placeholders for spacing.
 */
function isPlaceholderIcon(icon: ReactElement | undefined): boolean {
  if (!icon) return true
  if (icon.type === Fragment) return true
  if (icon.type === Box) return true
  return false
}

/**
 * Returns the icon if it's meaningful, otherwise undefined
 */
function getMeaningfulIcon(icon: ReactElement | undefined): ReactElement | undefined {
  return isPlaceholderIcon(icon) ? undefined : icon
}

/**
 * Flattens the sidebar nav (a flat list of groups, each with a flat list of NavLinks)
 * into a list of FlattenedNavLinks ready for the Cmd+K page index.
 *
 * For each link we:
 * - Inherit the parent group's title as `breadcrumb` (rendered as the page sub-label)
 * - Inherit the parent group's ACL (`action` / `subject`) when the link doesn't define its own
 * - Keep only the link's own meaningful icon. We intentionally do NOT fall back to the
 *   group icon — child links are text-only, so falling back would render the same group
 *   icon next to every result in that group, creating visual noise. The breadcrumb
 *   sub-label already disambiguates which group a result belongs to.
 * - Skip external links, openInNewTab links, and items without a usable path
 */
export default function flattenNavItems(navItems: NavItems): FlattenedNavLink[] {
  const result: FlattenedNavLink[] = []

  for (const group of navItems) {
    for (const item of group.items) {
      if (item.openInNewTab || item.externalLink) continue
      if (typeof item.path !== 'string' || item.path.length === 0) continue

      result.push({
        ...item,
        path: item.path,
        action: item.action ?? group.action,
        subject: item.subject ?? group.subject,
        icon: getMeaningfulIcon(item.icon),
        breadcrumb: group.title
      })
    }
  }

  return result
}
